import { useMemo } from 'react';

import { getFragmentData } from '@e-socna/data-layer';
import type { VatId } from '@e-socna/data-layer';
import { isDefined } from '@e-socna/utils';

import { DocumentEditorResolvers_QuickAccess_Mission_Fragment } from '../../../components/DocumentEditor/resolvers/fragments/quick-access.fragment';
import { getLiveServiceUsagesGroups } from '../../../components/DocumentEditor/resolvers/utils/mission-service-usages-groups.util';
import { useMissionReadyContext } from '../../../contexts/MissionContext/hooks/useMissionReadyContext.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { buildEffectiveAggregationLevelGroups } from '../../../forms/MissionAccountingInfoState/utils/build-aggregation-level-groups.util';
import { isServiceUsageAggregationLevelAvailable } from '../../../forms/MissionAccountingInfoState/utils/aggregation-level-options.util';
import { computeAggregationLevelVatWarnings } from '../../../forms/MissionAccountingInfoState/utils/compute-aggregation-level-vat-warnings.util';
import { getQuotationOptionGroups } from '../../../forms/MissionQuotationInfoState/utils/quotation-option-groups.util';
import { quotationActorsBlockingIssues } from '../../MissionQuotation/utils/quotation-actors-blocking-issues.util';
import type { ComplianceIssues } from '../../../components/ComplianceDialog';

import { MISSING_OPTIONS_CHOICE_MESSAGE } from '../utils/quotation-options-reminder.util';

const SERVICE_USAGE_LEVEL_UNAVAILABLE_NOTICE =
  "Le niveau d'agrégation Pennylane par prestation nécessite qu'au moins un groupe soit en mode automatique. Ce n'est pas le cas. Le niveau par groupe sera donc utilisé par défaut.";

export const useMissionAccountingComplianceIssues = (): ComplianceIssues => {
  const { mission } = useMissionReadyContext();
  const { vatsIdMap } = useReferencesContext();

  return useMemo(() => {
    const quickAccess = getFragmentData(
      DocumentEditorResolvers_QuickAccess_Mission_Fragment,
      mission,
    );

    const { quotationEmitterActor, quotationReceiverActor } =
      quickAccess.consolidation;
    const liveGroups = getLiveServiceUsagesGroups(quickAccess);

    const groups = buildEffectiveAggregationLevelGroups({
      selectedOptionGroupIds:
        quickAccess.quotationInfo?.snapshot?.selectedOptionGroupIds,
      liveGroups,
      snapshotGroups: quickAccess.quotationInfo?.snapshot?.serviceUsagesGroups,
    });

    const hasMissingOptionsChoice =
      !quickAccess.quotationInfo?.snapshot?.selectedOptionGroupIds &&
      !!getQuotationOptionGroups({
        liveGroups,
        snapshotGroups:
          quickAccess.quotationInfo?.snapshot?.serviceUsagesGroups,
      }).length;

    const isServiceUsageLevelAvailable =
      isServiceUsageAggregationLevelAvailable(groups);

    const vatWarningsByLevel = computeAggregationLevelVatWarnings({
      groups,
      resolveVatRateLabel: (vatId) =>
        vatsIdMap[vatId as VatId]?.label ?? 'TVA inconnue',
    });

    const defaultLevelVatWarnings = isServiceUsageLevelAvailable
      ? vatWarningsByLevel.ServiceUsage
      : vatWarningsByLevel.Group;

    return {
      blocking: [
        ...quotationActorsBlockingIssues(quickAccess.consolidation),
        !quickAccess.quotationInfo?.snapshot
          ? {
              message:
                "Le devis n'a pas été généré : la synchronisation avec Pennylane sera refusée.",
            }
          : null,
        quotationEmitterActor.thirdParty.__typename === 'Organization' &&
        !quotationEmitterActor.thirdParty.pennylaneUserAccountId
          ? {
              message:
                "L'organisation émettrice du devis n'a pas de compte Pennylane configuré.",
            }
          : null,
      ].filter(isDefined),
      warnings: [
        quotationReceiverActor.thirdParty.__typename === 'Person' &&
        !quotationReceiverActor.thirdParty.firstName
          ? 'Le destinataire du devis (personne physique) n\'a pas de prénom renseigné : la valeur "INCONNU" sera utilisée par défaut lors de la synchronisation avec Pennylane.'
          : null,
        isServiceUsageLevelAvailable
          ? null
          : SERVICE_USAGE_LEVEL_UNAVAILABLE_NOTICE,
        hasMissingOptionsChoice ? MISSING_OPTIONS_CHOICE_MESSAGE : null,
        ...defaultLevelVatWarnings,
      ]
        .filter(isDefined)
        .map((message) => ({ message })),
    };
  }, [mission, vatsIdMap]);
};
